import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, BookOpen } from 'lucide-react';
import toast from 'react-hot-toast';
import API from '../utils/api';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import ImageUploader from '../components/ImageUploader';
import { useImageUpload } from '../hooks/useImageUpload';

const cats = {
  construction:     'Building Construction',
  restaurant:       'Restaurant Job',
  nursing:          'Nursing',
  driving:          'Driving',
  sewing_operator:  'Garment Sewing Operator(Female Only)',
  linking_operator: 'Linking Operator(Female Only)',
  other:            'EDC & Others',
};

const catColors = {
  construction:     'bg-blue-100 text-blue-700',
  restaurant:       'bg-yellow-100 text-yellow-700',
  nursing:          'bg-orange-100 text-orange-700',
  driving:          'bg-green-100 text-green-700',
  sewing_operator:  'bg-purple-100 text-purple-700',
  linking_operator: 'bg-stone-100 text-stone-700',
  other:            'bg-slate-100 text-slate-700',
};

const empty = {
  name: '', category: 'construction', duration: '', fee: '',
  description: '', thumbnail: '', thumbnailPublicId: '', isActive: true,
};

export default function Courses() {
  const [courses, setCourses]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [modalOpen, setModalOpen]     = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId]       = useState(null);
  const [form, setForm]               = useState(empty);
  const [editing, setEditing]         = useState(false);
  const { upload, uploading, preview, setPreview } = useImageUpload('/upload/course-thumbnail');

  const fetchCourses = async () => { setLoading(true); const { data } = await API.get('/courses'); setCourses(data); setLoading(false); };
  useEffect(() => { fetchCourses(); }, []);

  const openCreate = () => { setForm(empty); setPreview(''); setEditing(false); setModalOpen(true); };
  const openEdit   = (c) => { setForm(c); setPreview(c.thumbnail || ''); setEditing(true); setModalOpen(true); };

  const handleImageUpload = async (file) => {
    const r = await upload(file);
    if (r) setForm(f => ({ ...f, thumbnail: r.imageUrl, thumbnailPublicId: r.publicId }));
  };
  const handleImageRemove = () => { setPreview(''); setForm(f => ({ ...f, thumbnail: '', thumbnailPublicId: '' })); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) { await API.put(`/courses/${form._id}`, form); toast.success('Course updated!'); }
      else         { await API.post('/courses', form);             toast.success('Course created!'); }
      setModalOpen(false); fetchCourses();
    } catch (err) { toast.error(err.response?.data?.message || 'Something went wrong!'); }
  };

  const handleDelete = async () => {
    try { await API.delete(`/courses/${deleteId}`); toast.success('Course deleted!'); setConfirmOpen(false); fetchCourses(); }
    catch { toast.error('Could not delete!'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
            <BookOpen className="text-green-600" size={22} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-800">Course Management</h1>
            <p className="text-slate-500 text-sm">Manage all courses</p>
          </div>
        </div>
        <button onClick={openCreate} className="flex items-center gap-2 bg-red-700 hover:bg-red-800 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
          <Plus size={18} /> New Course
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-4 border-red-700 border-t-transparent" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {courses.length === 0
            ? <div className="col-span-3 bg-white rounded-xl border border-slate-100 text-center py-12 text-slate-400">No courses found. Create one!</div>
            : courses.map(c => (
              <div key={c._id} className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden flex flex-col">
                {/* ── Thumbnail: 16:9 aspect ratio, img tag ── */}
                <div className="relative w-full overflow-hidden bg-slate-100" style={{ paddingTop: '56.25%' }}>
                  {c.thumbnail ? (
                    <img
                      src={c.thumbnail}
                      alt={c.name}
                      className="absolute inset-0 w-full h-full"
                      style={{ objectFit: 'cover', objectPosition: 'center' }}
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <BookOpen size={32} className="text-slate-300" />
                    </div>
                  )}
                </div>

                <div className="p-4 flex flex-col flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${catColors[c.category] || 'bg-slate-100 text-slate-600'}`}>
                      {cats[c.category] || c.category}
                    </span>
                    <div className="flex gap-1">
                      <button onClick={() => openEdit(c)} className="bg-green-50 hover:bg-green-100 text-green-700 p-1.5 rounded-lg transition-colors"><Pencil size={13} /></button>
                      <button onClick={() => { setDeleteId(c._id); setConfirmOpen(true); }} className="bg-red-50 hover:bg-red-100 text-red-600 p-1.5 rounded-lg transition-colors"><Trash2 size={13} /></button>
                    </div>
                  </div>
                  <h3 className="font-semibold text-slate-800 text-sm mb-1">{c.name}</h3>
                  <p className="text-slate-500 text-xs mb-3 flex-1 line-clamp-2">{c.description}</p>
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-500">⏱ {c.duration}</span>
                    <span className="font-bold text-green-600">৳ {Number(c.fee).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            ))}
        </div>
      )}

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Edit Course' : 'New Course'} size="lg">
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Course Name *</label>
            <input className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
              required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Category *</label>
              <select className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                {Object.entries(cats).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Duration *</label>
              <input className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                placeholder="e.g. 6 months" required value={form.duration} onChange={e => setForm({ ...form, duration: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Fee (BDT) *</label>
            <input type="number" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
              required value={form.fee} onChange={e => setForm({ ...form, fee: e.target.value })} />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
            <textarea className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
              rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
          </div>
          {/* hint prop — size recommendation shown in uploader */}
          <ImageUploader
            label="Course Thumbnail"
            hint="1280 × 720 px recommended (16:9 ratio)"
            currentUrl={preview}
            onUpload={handleImageUpload}
            onRemove={handleImageRemove}
            uploading={uploading}
          />
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => setModalOpen(false)} className="flex-1 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-medium text-sm">Cancel</button>
            <button type="submit" className="flex-1 px-4 py-2 bg-red-700 hover:bg-red-800 text-white rounded-lg font-medium text-sm">Save</button>
          </div>
        </form>
      </Modal>
      <ConfirmDialog isOpen={confirmOpen} onConfirm={handleDelete} onCancel={() => setConfirmOpen(false)} />
    </div>
  );
}
